import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Flex, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { bankTransferWallet } from "../groupsApi";
import { BANK_TRANSFER_CURRENCIES, BANK_TRANSFER_DESTINATIONS } from "../groupsConstants";
import { InfoBanner } from "../groupsUi";

/**
 * Builds the initial form state from the wallet currency.
 * @param {Object} wallet - Wallet entity
 * @returns {Object}
 */
function buildInitialForm(wallet) {
    const currency = (wallet?.currency || "eur").toLowerCase();
    return {
        amount: "",
        currency: BANK_TRANSFER_CURRENCIES.includes(currency) ? currency : "eur",
        destination: "group",
        member_id: "",
    };
}

/**
 * Validates the form.
 * @param {Object} form - Form state
 * @param {Object} wallet - Wallet entity (for balance check)
 * @returns {string|null}
 */
function validateForm(form, wallet) {
    const amount = Number(form.amount);
    if (!form.amount || Number.isNaN(amount) || amount <= 0) return "Le montant doit être supérieur à zéro.";
    const balance = Number(wallet?.balance ?? 0);
    if (amount > balance) return `Montant supérieur au solde disponible (${balance.toFixed(2)}).`;
    if (!form.currency) return "La devise est requise.";
    if (!form.destination) return "Le destinataire est requis.";
    if (form.destination === "member" && !form.member_id) return "Sélectionnez un membre destinataire.";
    return null;
}

/**
 * Builds the API payload from the form state.
 * @param {Object} form - Form state
 * @returns {Object}
 */
function buildPayload(form) {
    const payload = {
        amount: Number(form.amount),
        currency: form.currency,
        destination: form.destination,
    };
    if (form.destination === "member") payload.member_id = Number(form.member_id);
    return payload;
}

/**
 * Submits the transfer and surfaces toasts.
 * @param {Object} args - Submission args
 */
async function submitTransfer({ groupId, form, wallet, setLoading, onClose, onTransferred }) {
    const err = validateForm(form, wallet);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
        await bankTransferWallet(groupId, buildPayload(form));
        toast.success("Virement initié");
        onTransferred?.();
        onClose();
    } catch (error) {
        const msg = error?.response?.data;
        toast.error(typeof msg === "string" ? msg : "Échec du virement");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders the member selector (visible only when destination is "member").
 * @param {Object} props - { value, onChange, members }
 */
function MemberSelect({ value, onChange, members }) {
    const eligible = (members || []).filter((m) => m?.user_id);
    return (
        <FormControl>
            <FormLabel fontSize="0.8rem">Membre destinataire</FormLabel>
            <Select size="sm" borderRadius="8px" value={value} onChange={(e) => onChange(e.target.value)}
                placeholder={eligible.length === 0 ? "Aucun membre lié à un compte utilisateur" : "Sélectionner"}>
                {eligible.map((m) => (
                    <option key={m.id} value={m.id}>
                        {m.surname || m.email || `#${m.id}`}{m.is_leader ? " — Leader" : ""}
                    </option>
                ))}
            </Select>
        </FormControl>
    );
}

/**
 * Body of the bank transfer dialog.
 * @param {Object} props - { wallet, members, form, onChange }
 */
function DialogBody({ wallet, members, form, onChange }) {
    const textColor = useColorModeValue("secondaryGray.800", "whiteAlpha.900");
    const balance = Number(wallet?.balance ?? 0).toFixed(2);
    const walletCurrency = (wallet?.currency || "").toUpperCase();
    return (
        <Stack spacing="16px">
            <InfoBanner>
                Le virement est traité via Stripe Connect (payout). Le solde sera débité immédiatement.
            </InfoBanner>
            <Text color={textColor} fontSize="0.85rem">Solde disponible : <strong>{balance} {walletCurrency}</strong></Text>
            <Flex direction={{ base: "column", sm: "row" }} gap="12px">
                <FormControl isRequired>
                    <FormLabel fontSize="0.8rem">Montant</FormLabel>
                    <Input size="sm" borderRadius="8px" type="number" min={0} step={0.01}
                        value={form.amount} onChange={(e) => onChange("amount", e.target.value)} />
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="0.8rem">Devise</FormLabel>
                    <Select size="sm" borderRadius="8px" value={form.currency} onChange={(e) => onChange("currency", e.target.value)}>
                        {BANK_TRANSFER_CURRENCIES.map((c) => (<option key={c} value={c}>{c.toUpperCase()}</option>))}
                    </Select>
                </FormControl>
            </Flex>
            <FormControl>
                <FormLabel fontSize="0.8rem">Destinataire</FormLabel>
                <Select size="sm" borderRadius="8px" value={form.destination} onChange={(e) => onChange("destination", e.target.value)}>
                    {BANK_TRANSFER_DESTINATIONS.map((d) => (<option key={d.value} value={d.value}>{d.label}</option>))}
                </Select>
            </FormControl>
            {form.destination === "member" && (
                <MemberSelect value={form.member_id} onChange={(v) => onChange("member_id", v)} members={members} />
            )}
        </Stack>
    );
}

/**
 * Dialog allowing admins to initiate a Stripe payout from the group wallet.
 * @param {Object} props - { open, groupId, wallet, members, onClose, onTransferred }
 */
function BankTransferDialog({ open, groupId, wallet, members, onClose, onTransferred }) {
    const [form, setForm] = useState(buildInitialForm(wallet));
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setForm(buildInitialForm(wallet)); }, [open, wallet]);

    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onSubmit = () => submitTransfer({ groupId, form, wallet, setLoading, onClose, onTransferred });

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Virement bancaire</ModalHeader>
                <ModalCloseButton />
                <ModalBody><DialogBody wallet={wallet} members={members} form={form} onChange={onChange} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="brand" onClick={onSubmit} isLoading={loading} loadingText="Virement...">Lancer le virement</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default BankTransferDialog;
